import { addMinutes, format } from 'date-fns';
import { getCurrentInstance } from 'vue';
import { IWorkflowManager } from 'workflow-compiler-provider';
import { isMobile as isMobileAgentCheck } from 'mobile-device-detect';
import { app } from '@/main';
import { NavigationProvider } from '@/modules/navigation/providers/navigationProvider';
import { IgcSnackbarService } from '@/shared/plugins/SnackbarPlugin/IgcSnackbarService';
import { IgcStatisticsService } from '@/shared/plugins/StatisticsPlugin/IgcStatisticsService';
import { TThemeService } from '@/shared/services/ThemeService';
import { IgcModalProps } from '@/shared/plugins/ModalPlugin/IgcModalService.model';

export function decodeJwt(jwt: string) {
  const base64Url = jwt.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
    const d = (`00${c.charCodeAt(0).toString(16)}`).slice(-2);

    return `%${d}`;
  }).join(''));

  return JSON.parse(jsonPayload);
}

export function capitalize(str: string) {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

const MOBILE_SIZES = {
  EXTRA_SMALL_DEVICE: {
    MAX_HEIGHT: 812,
    MAX_WIDTH: 362,
  },
  SMALL_DEVICE: {
    MAX_HEIGHT: 812,
  },
};

export const DESKTOP_VIEW_WIDTH = 1100;

export function isSmallDevice() {
  return window.screen.height < MOBILE_SIZES.SMALL_DEVICE.MAX_HEIGHT;
}

export function isExtraSmallDevice() {
  return window.screen.width < MOBILE_SIZES.EXTRA_SMALL_DEVICE.MAX_WIDTH
    && window.screen.height < MOBILE_SIZES.EXTRA_SMALL_DEVICE.MAX_HEIGHT;
}

export function isMobile() {
  return isMobileAgentCheck || (window.matchMedia('(pointer: coarse)').matches && document.documentElement.clientWidth < DESKTOP_VIEW_WIDTH);
}

export function csvToArray(csv: string, delimiter = ',', validatedHeaders: string[] = []): {
  headers: string[],
  rows: Record<string, string>[],
} {
  const str = csv.replaceAll(/\r\n/g, '\n').replace(/(\r\n|\n)$/g, '');
  const hasRows = str.indexOf('\n') > 0;
  // slice from start of text to the first \n index
  // use split to create an array from string by delimiter
  const headers = (hasRows ? str.slice(0, str.indexOf('\n')) : str).split(delimiter);
  let rowsMapped: Record<string, string>[] = [];

  if (hasRows) {
    // slice from \n index + 1 to the end of the text
    // use split to create an array of each csv value row
    const rowData = str.slice(str.indexOf('\n') + 1);
    const rows = rowData.split('\n');

    // Map the rows
    // split values from each row into an array
    // use headers.reduce to create an object
    // object properties derived from headers:values
    // the object passed as an element of the array
    // return the array
    rowsMapped = rows.map((row, rowIndex) => {
      const values = row.split(delimiter);

      if (values.length !== headers.length) {
        throw new Error(`CSV validation error: Count of headers does not match count of columns of row ${rowIndex + 1}.`);
      }

      return headers.reduce((object: { [index: string]: string }, header, index) => {
        object[header] = values[index];

        return object;
      }, {});
    });
  }

  if (!validatedHeaders.length) {
    return {
      headers,
      rows: rowsMapped,
    };
  }

  if (validatedHeaders.length !== headers.length) {
    throw new Error(`CSV validation error: Expected ${validatedHeaders.length} columns, but got ${headers.length}`);
  }

  validatedHeaders.forEach((name, index) => {
    if (name.toLowerCase() !== headers[index].toLowerCase()) {
      throw new Error(`CSV validation error: Expected column ${name} column, but got ${headers[index]}`);
    }
  });

  return {
    headers,
    rows: rowsMapped,
  };
}

function getAppContext() {
  return getCurrentInstance()?.appContext || app;
}

function getGlobalProperties() {
  return getAppContext().config.globalProperties;
}

export function formatIsoToUtc(date: Date | string, display = 'yyyy-MM-dd') {
  const normalizedDate = date instanceof Date ? date : new Date(date);

  return format(addMinutes(normalizedDate, normalizedDate.getTimezoneOffset()), display || 'yyyy-MM-dd');
}

export function useIgcModal(): { hideModal: () => void, showModal: (options: IgcModalProps, modalParams?: any, hooks?: any) => Promise<any> } {
  const modal = getGlobalProperties().$igcModal;

  return {
    hideModal: modal.hide,
    showModal: modal.show,
  };
}

export function useWorkflowManager(): IWorkflowManager {
  return getGlobalProperties().$workflowManager;
}

export function useNavigationProvider(): NavigationProvider {
  return getGlobalProperties().$navigationProvider;
}

export function useFlow(): any {
  return getGlobalProperties().$flow;
}

export function useIgcSnackbar(): IgcSnackbarService {
  return getGlobalProperties().$igcSnackbar;
}

export function useIgcStatistics(): IgcStatisticsService {
  return getGlobalProperties().$igcStatistics;
}

export function useTheme(): TThemeService {
  return getGlobalProperties().$theme;
}

export function scrollToFirstValidationError(selector = '.validation-error') {
  const element = document.querySelector(`${selector}:not([style*="display: none"])`);

  element?.scrollIntoView({ behavior: 'smooth' });
}
